import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useNumbers } from '../context/NumberContext';
import api from '../api';

const navItems = [
  { path: '/businesses', label: 'Businesses', icon: '🏢' },
  { path: '/inbox',      label: 'Inbox',      icon: '📥' },
  { path: '/send',       label: 'Send',       icon: '📤' },
  { path: '/templates',  label: 'Templates',  icon: '📋' },
  { path: '/flows',      label: 'Bot Flows',  icon: '🤖' },
  { path: '/analytics',  label: 'Analytics',  icon: '📊' },
  { path: '/webhooks',   label: 'Webhooks',   icon: '🔗' },
  { path: '/api-docs',   label: 'API Docs',   icon: '📖' },
  { path: '/settings',   label: 'Settings',   icon: '⚙️' },
  { path: '/users',      label: 'Users',      icon: '👥', adminOnly: true },
];

function useIsMobile() {
  const [v, setV] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const h = () => setV(window.innerWidth <= 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return v;
}

export default function Layout() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const isMobile  = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userRole, setUserRole]       = useState('user');
  const { businesses, activeBusiness, selectBusiness,
          numbers, activeNumber, selectNumber, reset } = useNumbers();

  useEffect(() => {
    setSidebarOpen(false);
    api.get('/auth/me').then(r => setUserRole(r.data.role)).catch(() => {});
  }, [location.pathname]);

  const logout = () => {
    localStorage.removeItem('wa_token');
    localStorage.removeItem('wa_active_biz_id');
    localStorage.removeItem('wa_active_num_id');
    reset();
    navigate('/login');
  };

  const currentPage = navItems.find(n => location.pathname.startsWith(n.path));
  const visibleNav  = navItems.filter(n => !n.adminOnly || userRole === 'admin');

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>

      {isMobile && sidebarOpen && (
        <div onClick={() => setSidebarOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 40 }} />
      )}

      <aside style={{
        width: 240, background: 'var(--bg2)', borderRight: '1px solid var(--border)',
        display: 'flex', flexDirection: 'column', flexShrink: 0, zIndex: 50,
        ...(isMobile ? {
          position: 'fixed', top: 0, left: 0, bottom: 0,
          transform: sidebarOpen ? 'translateX(0)' : 'translateX(-100%)',
          transition: 'transform 0.25s ease',
        } : {}),
      }}>
        {/* Brand */}
        <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--accent)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17, flexShrink: 0 }}>💬</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.06em' }}>WA DASHBOARD</div>
            <div style={{ fontSize: 10, color: 'var(--text3)' }}>Multi-User · Multi-Business</div>
          </div>
          {isMobile && (
            <button onClick={() => setSidebarOpen(false)}
              style={{ background: 'none', border: 'none', color: 'var(--text2)', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}>✕</button>
          )}
        </div>

        {/* ── Business selector ── */}
        {businesses.length > 0 && (
          <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)' }}>
            <div style={{ fontSize: 9, color: 'var(--text3)', fontFamily: 'var(--mono)', marginBottom: 5, letterSpacing: '0.05em' }}>BUSINESS</div>
            <select value={activeBusiness?.id || ''}
              onChange={e => {
                const b = businesses.find(x => x.id === parseInt(e.target.value));
                if (b) selectBusiness(b);
              }}
              style={{ width: '100%', fontSize: 12, padding: '5px 8px', background: 'var(--bg3)',
                border: '1px solid var(--border2)', borderRadius: 'var(--r-sm)', color: 'var(--text)', cursor: 'pointer' }}>
              {businesses.map(b => (
                <option key={b.id} value={b.id}>{b.label}</option>
              ))}
            </select>
          </div>
        )}

        {/* ── Number selector ── */}
        {numbers.length > 0 && (
          <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)' }}>
            <div style={{ fontSize: 9, color: 'var(--text3)', fontFamily: 'var(--mono)', marginBottom: 5, letterSpacing: '0.05em' }}>NUMBER</div>
            <select value={activeNumber?.id || ''}
              onChange={e => {
                const n = numbers.find(x => x.id === parseInt(e.target.value));
                if (n) selectNumber(n);
              }}
              style={{ width: '100%', fontSize: 12, padding: '5px 8px', background: 'var(--bg3)',
                border: `1px solid var(--accent)`, borderRadius: 'var(--r-sm)',
                color: 'var(--accent)', cursor: 'pointer', fontWeight: 600 }}>
              {numbers.map(n => (
                <option key={n.id} value={n.id}>{n.label}{n.display_phone ? ` · ${n.display_phone}` : ''}</option>
              ))}
            </select>
          </div>
        )}

        {/* ── No business/number yet prompt ── */}
        {businesses.length === 0 && (
          <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--border)' }}>
            <div style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.5 }}>
              No business added yet.
              <br />
              <button onClick={() => navigate('/businesses')}
                style={{ background: 'none', border: 'none', color: 'var(--accent)', fontSize: 11,
                  cursor: 'pointer', padding: 0, marginTop: 4, textDecoration: 'underline' }}>
                + Add Business →
              </button>
            </div>
          </div>
        )}

        {/* Nav */}
        <nav style={{ flex: 1, padding: '10px 8px', overflowY: 'auto' }}>
          {visibleNav.map(item => (
            <NavLink key={item.path} to={item.path}
              style={({ isActive }) => ({
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 12px', borderRadius: 'var(--r-sm)',
                marginBottom: 2, textDecoration: 'none',
                color: isActive ? 'var(--accent)' : 'var(--text2)',
                background: isActive ? 'var(--accent-dim)' : 'transparent',
                fontSize: 13, fontWeight: isActive ? 600 : 400, transition: 'all 0.15s',
              })}>
              <span style={{ fontSize: 16 }}>{item.icon}</span>
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* Logout */}
        <div style={{ padding: '8px 8px', borderTop: '1px solid var(--border)' }}>
          <button onClick={logout}
            style={{ width: '100%', background: 'transparent', color: 'var(--text3)', border: 'none',
              padding: '10px 12px', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 10,
              borderRadius: 'var(--r-sm)', fontSize: 13, cursor: 'pointer' }}
            onMouseEnter={e => e.currentTarget.style.color = 'var(--danger)'}
            onMouseLeave={e => e.currentTarget.style.color = 'var(--text3)'}>
            <span style={{ fontSize: 16 }}>🚪</span> Logout
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
        {isMobile && (
          <div style={{ height: 50, background: 'var(--bg2)', borderBottom: '1px solid var(--border)',
            display: 'flex', alignItems: 'center', padding: '0 12px', gap: 10, flexShrink: 0 }}>
            <button onClick={() => setSidebarOpen(true)}
              style={{ background: 'none', border: 'none', color: 'var(--text)', fontSize: 22, cursor: 'pointer', padding: '4px 8px' }}>☰</button>
            <span style={{ fontSize: 16 }}>{currentPage?.icon}</span>
            <span style={{ fontWeight: 600, fontSize: 14 }}>{currentPage?.label || 'Dashboard'}</span>
            {activeNumber && (
              <div style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--accent)',
                background: 'var(--accent-dim)', padding: '3px 8px', borderRadius: 4,
                maxWidth: 110, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {activeNumber.label}
              </div>
            )}
          </div>
        )}
        <main style={{ flex: 1, overflow: 'auto', background: 'var(--bg)' }}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
